import React, { useCallback } from 'react';
import { createStyles, useTheme } from '@/contexts/theme-context';
import { Pressable, View } from 'react-native';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useCartStore } from '@/experiences/sales-portal/domain/ordering/blocs/stores/cart-store';
import { VariantPackQuantityInput } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/item-detail-content/details-tab/variant-pack/variant-pack-quantity-input';
import type { RelatedVariantPack } from '@/experiences/sales-portal/domain/items/blocs/transformers/related-variant-packs-transformer';

export interface VariantPackCardProps {
  isQuantityDisabled?: boolean;
  onSaveCart: () => Promise<unknown>;
  onSelect: (variantPackId: string) => void;
  variantPack: RelatedVariantPack;
}

/**
 * One bordered row in the Details tab's variant-pack list. The left region
 * (subtitle + price) is pressable and drills the panel into this pack; the
 * right region holds a cart-wired quantity input.
 *
 * @param props - {@link VariantPackCardProps}.
 * @returns The rendered card.
 */
export const VariantPackCard: React.FC<VariantPackCardProps> = ({
  isQuantityDisabled,
  onSaveCart,
  onSelect,
  variantPack,
}) => {
  const [styles] = useStyles();
  const theme = useTheme();

  const handlePress = () => {
    onSelect(variantPack.variantPackId);
  };

  const handleUpdateQuantity = useCallback(
    (quantity: number) =>
      useCartStore
        .getState()
        .updateCartItemQuantity(variantPack.variantPackId, quantity),
    [variantPack.variantPackId],
  );

  return (
    <View style={styles.card}>
      <Pressable
        accessibilityRole="button"
        onPress={handlePress}
        style={styles.pressableContent}
      >
        <Typography
          color={theme.sk.color.text.secondary}
          numberOfLines={1}
          variant="paragraph-md"
        >
          {variantPack.subtitle}
        </Typography>
        <Typography
          color={theme.sk.color.text.primary}
          numberOfLines={1}
          variant="paragraph-md-600"
        >
          {variantPack.priceText}
        </Typography>
      </Pressable>
      <VariantPackQuantityInput
        isQuantityDisabled={isQuantityDisabled}
        onSaveCart={onSaveCart}
        onUpdateQuantity={handleUpdateQuantity}
        variantPackUUID={variantPack.variantPackId}
      />
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  card: {
    alignItems: 'center',
    borderColor: theme.sk.color.border.primary,
    borderRadius: theme.sk.radii.radiusMedium,
    borderWidth: 1,
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSMedium,
    justifyContent: 'space-between',
    paddingHorizontal: theme.sk.spacing.spaceSMedium,
    paddingVertical: theme.sk.spacing.spaceSmall,
  },
  pressableContent: {
    flex: 1,
    flexDirection: 'column',
  },
}));
